/**
 * GroupNameModal — a single-field (name) modal used for both "New group" and
 * "Rename group". One text input, a primary submit, and a cancel.
 *
 * Rule 8: `onSubmit` is an async mutate; failures surface as inline error copy
 * (no banned words, no exclamations — branding.md §5.2.1) AND re-enable retry.
 * The modal never closes on a failed submit — the user can see it did not save.
 *
 * Rule 7: full-width bottom sheet on mobile, centered card ≥640px; 44px targets.
 * design.md §2: hairline border, one accent on the focus ring + primary CTA.
 */

import { useEffect, useRef, useState, type FormEvent } from "react";
import { resolveMutateErrorCopy } from "@proof/client-core/errors";
import { injectGroupStyles } from "./styles";

export interface GroupNameModalProps {
  /** Modal chrome copy. */
  title: string;
  /** Primary button label, e.g. "Create" / "Save". */
  submitLabel: string;
  /** Pre-filled value (rename); empty for create. */
  initialValue?: string;
  /** Placeholder shown when empty. */
  placeholder?: string;
  /** Async mutate. Resolve → modal closes; reject → inline error, stays open. */
  onSubmit: (name: string) => Promise<void>;
  onClose: () => void;
}

export function GroupNameModal({
  title,
  submitLabel,
  initialValue = "",
  placeholder = "Family",
  onSubmit,
  onClose,
}: GroupNameModalProps) {
  injectGroupStyles();
  const [value, setValue] = useState(initialValue);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Escape closes (only when not mid-submit).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !busy) onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const name = value.trim();
    if (name.length === 0) {
      setError("Enter a name.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSubmit(name);
      // Success: caller unmounts the modal.
    } catch (err) {
      // Rule 8 — surface the failure; keep the modal open for retry.
      //
      // Prefer the SERVER's own message. This used to hard-code "Check your
      // connection and try again." and discard `err`, so hitting the free
      // owned-groups cap told the user their network had failed while the
      // console held the correct copy ("You've reached your plan's group
      // limit. Remove a group to continue.").
      console.error({ event: "group_name_modal_submit_failed", err });
      setError(resolveMutateErrorCopy(err, "That didn’t save. Try again."));
      setBusy(false);
    }
  }

  return (
    <div
      className="proof-group-modal-scrim"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <form
        className="proof-group-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onSubmit={handleSubmit}
        data-testid="group-name-modal"
      >
        <h2 className="proof-group-modal__title">{title}</h2>
        <label className="proof-group-modal__label" htmlFor="proof-group-name-input">
          Name
        </label>
        <input
          id="proof-group-name-input"
          ref={inputRef}
          className="proof-group-input"
          type="text"
          value={value}
          placeholder={placeholder}
          maxLength={80}
          disabled={busy}
          onChange={(e) => setValue(e.target.value)}
          data-testid="group-name-input"
        />
        {error !== null && (
          <p className="proof-group-modal__error" role="alert">
            {error}
          </p>
        )}
        <div className="proof-group-modal__actions">
          <button
            type="button"
            className="proof-group-btn"
            onClick={onClose}
            disabled={busy}
            data-testid="group-name-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="proof-group-btn proof-group-btn--primary"
            disabled={busy || value.trim().length === 0}
            data-testid="group-name-submit"
          >
            {busy ? "Saving…" : submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
}
